import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { get, isEmpty } from 'lodash';
import { noop, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { AsyncAutocompleteMultipleSelectsComponent } from '../../../../async-autocomplete-multiple-selects/async-autocomplete-multiple-selects.component';
import { AsyncAutocompleteComponent } from '../../../../async-autocomplete/async-autocomplete.component';
import { FormControlType, FormField, FormGroupComponent } from '../../../../forms';
import { FilterFieldOptions } from '../../../models';
import { DynamicListFieldService } from '../../../services/dynamic-list-filed.service';
import { AbstractFilterEngineFormComponent } from '../abstract-filter-form.component';

/**
 * Type representing a dropdown option (can be object or primitive)
 */
export type DropdownOption = Record<string, unknown> | string | number;

@Component({
  selector: 'iot-platform-ui-filter-dropdown-field',
  standalone: true,
  imports: [FlexLayoutModule, FormGroupComponent, AsyncAutocompleteComponent, AsyncAutocompleteMultipleSelectsComponent],
  templateUrl: './filter-dropdown-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterDropdownFieldComponent extends AbstractFilterEngineFormComponent<DropdownOption | DropdownOption[]> implements OnInit {
  readonly items = signal<DropdownOption[]>([]);
  readonly isLoading = signal<boolean>(false);

  fieldOptions: Signal<Partial<FilterFieldOptions>> = computed(() => {
    const field = this.field();
    const options = field?.options;
    if (options?.list) {
      // by default use 'key' and 'value' properties for static lists
      options.filterBy = get(options, 'filterBy', 'key');
      options.selectByProperty = get(options, 'selectByProperty', 'value');
      options.labelToDisplay = get(options, 'labelToDisplay', 'value');
    }
    return options;
  });

  isMultiSelect = computed(() => {
    const options = this.fieldOptions();
    return !!options?.multiSelect;
  });
  isAutocomplete = computed(() => {
    const options = this.fieldOptions();
    return !!options?.autocomplete;
  });
  selectedValues: WritableSignal<DropdownOption | DropdownOption[] | null> = signal(null);
  isResetInProgress = signal(false);
  protected readonly dynamicListFieldService: DynamicListFieldService = inject(DynamicListFieldService);

  constructor() {
    super();
    effect(() => this.initializeControl());
  }

  ngOnInit() {
    super.ngOnInit();
    this.loadOptions();
  }

  // Initialize select control with existing filter values for multi-select
  initializeControl() {
    const currentFilters = this.currentFilters();
    const isMultiSelect = this.isMultiSelect();
    const isAutocomplete = this.isAutocomplete();
    const items = this.items();
    const selectedOperator = this.selectedOperator();

    let selectedOptions: any = isMultiSelect ? [] : null;
    // Only pre-select for multi-select fields when we have data
    if (isMultiSelect && currentFilters.length > 0 && items.length > 0) {
      // Extract values from current filters
      const existingValues = currentFilters.map((f) => ({
        value: f.value,
        operator: f.criteriaOperator
      }));

      // Find matching options from the loaded options
      selectedOptions = items.filter((option: any) => {
        const optionValue = this.formatValue(option);
        const exist = existingValues.some(({ value, operator }) => value === optionValue && operator === selectedOperator?.value);
        if (isAutocomplete) {
          // set selected property when auto-apply is enabled
          option.selected = !!exist;
        }
        return exist;
      });
    }
    this.selectedValues.set(selectedOptions);
    // Set the control value without emitting events
    if (!isAutocomplete && this.searchTermControl) {
      this.searchTermControl?.setValue(selectedOptions, { emitEvent: false });
    }
  }

  getValueDisplay(option?: DropdownOption) {
    const options = this.fieldOptions();
    const isMultiSelect = this.isMultiSelect();
    const value = this.selectedValues();
    if (isMultiSelect) {
      if (option) {
        return options?.displayValueWrapper ? options.displayValueWrapper(option) : this.formatLabel(option);
      }
      return value;
    }
    return options?.displayValueWrapper ? options.displayValueWrapper(value) : this.formatLabel(value as DropdownOption);
  }

  getControlValue() {
    const value = this.selectedValues();
    const isMultiSelect = this.isMultiSelect();
    return isMultiSelect ? value : this.formatValue(value as DropdownOption);
  }

  hasValidValue() {
    const isMultiSelect = this.isMultiSelect();
    const value = this.selectedValues();
    return isMultiSelect ? true : !isEmpty(value);
  }

  compareFn(filter1: DropdownOption, filter2: DropdownOption): boolean {
    const options = this.fieldOptions();
    const filterBy = get(options, 'filterBy', 'value');
    return get(filter1, filterBy, filter1) === get(filter2, filterBy, filter2);
  }

  getSearchFields(): Partial<FormField>[] {
    const isAutocomplete = this.isAutocomplete();
    if (isAutocomplete) {
      return [];
    }
    return [
      {
        ...this.searchTermField,
        type: FormControlType.DROP_DOWN_LIST,
        items: this.items,
        multiple: this.isMultiSelect,
        disabled: computed(() => {
          if (this.isMultiSelect()) {
            return false;
          }
          return this.disabled();
        }),
        isOptionDisabled: (item: DropdownOption) => this.isOptionDisabled(item),
        trackBy: (item: DropdownOption) => this.formatValue(item),
        displayBy: (item: DropdownOption) => this.formatLabel(item),
        onReady: () => noop(),
        compareFn: (p1: DropdownOption, p2: DropdownOption) => this.compareFn(p1, p2),
        selectionChange: (event: any) => this.onSelectionChange(event),
        visible: this.requiresValue
      }
    ];
  }

  onSelectionChange(event: any) {
    if (this.isMultiSelect()) {
      if (this.isAutocomplete()) {
        this.handleSelectionChange(event.option.values);
      } else {
        this.handleSelectionChange(event.value);
      }
    } else if (this.isAutocomplete()) {
      this.handleSelectionChange(event.option.value);
    } else {
      this.handleSelectionChange(event.value);
    }
  }

  isOptionDisabled(item: DropdownOption) {
    const disabled = this.disabled();
    if (this.isMultiSelect()) {
      const values = this.selectedValues() as DropdownOption[];
      const isItemSelected = !!values?.some((e: DropdownOption) => this.formatValue(e) === this.formatValue(item));
      return !isItemSelected && disabled;
    }
    return disabled;
  }

  override clearValue(): void {
    super.clearValue();
    this.selectedValues.set(this.isMultiSelect() ? [] : null);
  }

  handleSelectionChange(values: DropdownOption | DropdownOption[] | null) {
    this.selectedValues.set(values);

    if (this.isAutoApply()) {
      this.handleApply();
    }
  }

  override handleReset(): void {
    this.isResetInProgress.set(true);
    super.handleReset();
  }

  /**
   * Emit multiple filters for multi-select dropdown
   * Sends all selected values as a batch in a single emission
   */
  override emitFilter(values: DropdownOption | DropdownOption[] | null, valueDisplay?: string): void {
    if (this.isMultiSelect()) {
      this.emitMultipleFilter((values ?? []) as DropdownOption[]);
    } else {
      super.emitFilter(values, valueDisplay);
    }
  }

  emitMultipleFilter(values: DropdownOption[]): void {
    const field = this.field();

    const operator = this.selectedOperator();
    if (!operator) {
      console.warn('[FilterField] No operator selected for field:', field.key);
      return;
    }

    if (!values || values.length === 0) {
      let clearAllForField = this.requiresValue();
      // If reset is in progress and field does not require value, do not clear all filters for this field
      if (this.isResetInProgress()) {
        clearAllForField = true;
      }
      // If no values selected, emit a special "clear all" signal for this field
      const clearSignal: any = {
        criteriaKey: field.options.criteriaKey,
        criteriaLabel: field.fullLabel,
        criteriaOperator: operator.value,
        value: null,
        valueDisplay: null,
        label: null,
        isMultiSelectBatch: true,
        fieldKey: field.key,
        // Special flag to clear all filters for this field
        clearAllForField
      };
      this.filterApplied.emit({ filter: clearSignal, field });
      this.isResetInProgress.set(false);
      return;
    }

    // Build array of all filter values
    const filterValues = values.map((option) => ({
      value: this.formatValue(option),
      valueDisplay: this.getValueDisplay(option)
    }));

    // Emit a single batch with all values
    const batchFilter: any = {
      criteriaKey: field.options.criteriaKey,
      criteriaLabel: field.fullLabel,
      criteriaOperator: operator.value,
      values: filterValues, // Array of all selected values with labels
      isMultiSelectBatch: true,
      fieldKey: field.key
    };

    this.filterApplied.emit({ filter: batchFilter, field });
  }

  formatLabel(value: DropdownOption) {
    const options = this.fieldOptions();
    if (options?.labelToDisplay) {
      return options?.displayWrapper ? options.displayWrapper(value) : get(value, options.labelToDisplay, value);
    }
    return value;
  }

  formatValue(value: DropdownOption) {
    const options = this.fieldOptions();
    let v: any = value;
    if (options?.filterBy) {
      v = get(value, options.filterBy, value);
    }
    if (v !== null && v !== undefined && options?.criteriaValueAsString) {
      return v.toString();
    }
    if (v === 'true') {
      return true;
    }
    if (v === 'false') {
      return false;
    }
    return v;
  }

  onDataLoaded(data: DropdownOption[]) {
    this.items.set(data);
  }

  /**
   * Load options from static values or API endpoint
   */
  loadOptions(): void {
    const options = this.fieldOptions();

    // Load from static values
    if (options?.list) {
      this.onDataLoaded(options.list as DropdownOption[]);
      return;
    }

    // Load from API endpoint
    if (options?.url) {
      this.isLoading.set(true);
      this.dynamicListFieldService
        .getDynamicList(options)
        .pipe(
          catchError((error) => {
            console.error('Error loading dropdown options:', error);
            return of([]);
          }),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((data) => {
          this.onDataLoaded(data);
        });
    }
  }
}
